/**
 * This store module implements Conviva SDK for video playback analytics.
 */
import Conviva from '@convivainc/conviva-js-coresdk/conviva-core-sdk';
import { ConvivaMetadata } from '@convivainc/conviva-js-coresdk';

// @ts-ignore
import ConvivaModule from '@convivainc/conviva-js-html5';

import Visibility from 'visibilityjs';
import { Action, action, ThunkOn, thunkOn, Thunk, thunk, State } from 'easy-peasy';
import { StoreState } from './store';
import { getAssetName, getCustomContentType } from '../utils/conviva-helpers';
import { PlayerStatus } from '../typings';
import pkg from '../../package.json';
import { Browser } from '../utils/browser';

export interface ConvivaState {
  enabled: boolean;
  videoAnalytics: Conviva.VideoAnalytics | null;
  contentInfo: ConvivaMetadata | null;
  visibilityChangeListenerId: number | boolean;
  enable: Thunk<ConvivaState, void, any, StoreState>;
  initialize: Thunk<ConvivaState, void, any, StoreState>;
  reset: Thunk<ConvivaState, void, any, StoreState>;
  createSession: Thunk<ConvivaState, void, any, StoreState>;
  destroySession: Thunk<ConvivaState, void, any, StoreState>;
  setEnabled: Action<ConvivaState, boolean>;
  registerVisibilityChangeListener: Action<ConvivaState>;
  unregisterVisibilityChangeListener: Action<ConvivaState>;
  setConvivaVideoAnalytics: Action<ConvivaState, Conviva.VideoAnalytics | null>;
  setContentInfo: Action<ConvivaState, ConvivaMetadata | null>;
  onConfigLoaded: ThunkOn<ConvivaState, void, StoreState>;
  onPlayerVideoChanged: ThunkOn<ConvivaState, PlayerStatus, StoreState>;
  onPlayerStatusChanged: ThunkOn<ConvivaState, PlayerStatus, StoreState>;
  onPlayerFramerateChanged: ThunkOn<ConvivaState, number, StoreState>;
  onPlayerBitrateChanged: ThunkOn<ConvivaState, number, StoreState>;
  onPlayerDestroy: ThunkOn<ConvivaState, void, StoreState>;
  onResumeOverlayVisible: ThunkOn<ConvivaState, boolean, StoreState>;
  onChromecastCasting: ThunkOn<ConvivaState, boolean, StoreState>;
  onError: ThunkOn<ConvivaState, Error, StoreState>;
}

const initialState: State<ConvivaState> = {
  enabled: false,
  videoAnalytics: null,
  contentInfo: null,
  visibilityChangeListenerId: false,
};

export const convivaState: ConvivaState = {
  /*********
   * State *
   *********/
  ...initialState,

  /***********
   * Actions *
   ***********/

  /**
   * Setups Conviva SDK.
   */
  enable: thunk(actions => {
    /**
     * Load conviva-js-html5 into global scope. This is required by conviva-core-sdk
     */
    if (window.ConvivaModule === undefined) {
      window.ConvivaModule = ConvivaModule;
    }

    actions.setEnabled(true);
  }),

  /**
   * Initializes the Conviva analytics instance
   */
  initialize: thunk((actions, _, { getStoreState }) => {
    const storeState = getStoreState();
    const debug = storeState.config.debug;
    const logging = storeState.config.logging;

    Conviva.Analytics.init(storeState.config.convivaCustomerKey!, null, {
      [Conviva.Constants.GATEWAY_URL]: debug
        ? `https://${storeState.config.convivaCustomerKey}.ts-testonly.conviva.com`
        : undefined,
      [Conviva.Constants.LOG_LEVEL]: logging ? Conviva.Constants.LogLevel.DEBUG : undefined,
    });

    Conviva.Analytics.setDeviceMetadata({
      [Conviva.Constants.DeviceMetadata.CATEGORY]: Conviva.Constants.DeviceCategory.WEB,
      [Conviva.Constants.DeviceMetadata.BRAND]: Browser.device.vendor,
      [Conviva.Constants.DeviceMetadata.MANUFACTURER]: Browser.device.vendor,
      [Conviva.Constants.DeviceMetadata.MODEL]: Browser.device.model,
      [Conviva.Constants.DeviceMetadata.TYPE.toString()]: Browser.device.type,
      [Conviva.Constants.DeviceMetadata.VERSION]: Browser.info.version,
      [Conviva.Constants.DeviceMetadata.OS_NAME]: Browser.os.name,
      [Conviva.Constants.DeviceMetadata.OS_VERSION]: Browser.os.version,
    });

    actions.registerVisibilityChangeListener();
  }),

  reset: thunk((actions, _, { getState }) => {
    const state = getState();
    actions.unregisterVisibilityChangeListener();

    if (state.videoAnalytics) {
      state.videoAnalytics.release();
    }

    Conviva.Analytics.release();
    Object.assign(state, { ...initialState });
  }),

  /**
   * Creates Conviva session.
   */
  createSession: thunk((actions, __, { getState, getStoreState }) => {
    const state = getState();
    const storeState = getStoreState();

    if (state.enabled) {
      const convivaVideoAnalytics = storeState.conviva.videoAnalytics || Conviva.Analytics.buildVideoAnalytics();

      const contentInfo = {
        [Conviva.Constants.STREAM_URL]: storeState.player.videoItem?.url || undefined,
        [Conviva.Constants.PLAYER_NAME]: 'bodi-web',
        [Conviva.Constants.FRAMEWORK_NAME]: 'Web Player',
        [Conviva.Constants.FRAMEWORK_VERSION]: pkg.version,
        [Conviva.Constants.VIEWER_ID]: storeState.config.userGuid || 'GUEST_USER',
        [Conviva.Constants.DeviceMetadata.CATEGORY]: Browser.userAgent,
        'c3.cm.contentType': getCustomContentType(storeState),
        [Conviva.Constants.APPLICATION_VERSION]: storeState.config.version,
      } as ConvivaMetadata;

      if (storeState.player.videoItem) {
        contentInfo['c3.cm.id'] = storeState.player.videoItem.guid;
        contentInfo[Conviva.Constants.ASSET_NAME] = getAssetName(storeState.player.videoItem, storeState);
      }

      convivaVideoAnalytics.setContentInfo(contentInfo);

      /** Only report play once per Conviva session */
      if (!storeState.conviva.videoAnalytics) {
        convivaVideoAnalytics.reportPlaybackRequested(contentInfo);
      }

      /**
       * Set the conviva video player.
       * The bod-video-player_html5_api DOM Element (VJS) is send to allow iPhone/Safari to send playback events
       * if VJS its not available yet, send the base bod-video-player DOM Element
       */
      const videoElement = storeState.player.videoEngine?.el().querySelector('video') || storeState.player.videoElement;
      if (videoElement) {
        convivaVideoAnalytics.setPlayer(videoElement);
      }

      actions.setConvivaVideoAnalytics(convivaVideoAnalytics);
      actions.setContentInfo(contentInfo);
    }
  }),

  /**
   * Destroys Conviva session.
   */
  destroySession: thunk(async (actions, _, { getState }) => {
    const state = getState();

    if (state.videoAnalytics) {
      state.videoAnalytics.reportPlaybackEnded();
      state.videoAnalytics.release();
      actions.setConvivaVideoAnalytics(null);
    }
  }),

  setEnabled: action((state, enabled) => {
    state.enabled = enabled;
  }),

  /**
   * Register an event listener to report when the player
   * changes between background/foreground
   */
  registerVisibilityChangeListener: action(state => {
    const id = Visibility.change(() => {
      Visibility.hidden() ? Conviva.Analytics.reportAppBackgrounded() : Conviva.Analytics.reportAppForegrounded();
    });

    state.visibilityChangeListenerId = id;
  }),

  /**
   * Unregister the event listener that reports when the player
   * changes between background/foreground
   */
  unregisterVisibilityChangeListener: action(state => {
    if (state.visibilityChangeListenerId !== false) {
      Visibility.unbind(state.visibilityChangeListenerId as number);
      state.visibilityChangeListenerId = false;
    }
  }),

  setConvivaVideoAnalytics: action((state, videoAnalytics) => {
    state.videoAnalytics = videoAnalytics;
  }),

  setContentInfo: action((state, contentInfo) => {
    state.contentInfo = contentInfo;
  }),

  /*************
   * Listeners *
   *************/

  /**
   * When the configuration loads, initialize Conviva client.
   */
  onConfigLoaded: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.config.load.successType];
    },
    function handler(actions, _, { getState }) {
      const state = getState();

      if (state.enabled) {
        actions.initialize();
      }
    }
  ),

  /**
   * When a new video is going to play, create a new Conviva session.
   */
  onPlayerVideoChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setVideoItem];
    },
    function handler(actions, _, { getState }) {
      const state = getState();

      if (state.enabled) {
        actions.createSession();
      }
    }
  ),

  /**
   * Handle video ending.
   */
  onPlayerStatusChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setStatus];
    },
    function handler(actions, event, { getState }) {
      const state = getState();

      if (state.enabled) {
        switch (event.payload) {
          case PlayerStatus.Ended:
          case PlayerStatus.Stopped:
            return actions.destroySession();
        }
      }
    }
  ),

  onPlayerFramerateChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setFramerate];
    },
    function handler(_, event, { getState }) {
      const state = getState();
      const framerate = event.payload;

      if (framerate && state.enabled && state.videoAnalytics) {
        state.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.RENDERED_FRAMERATE, framerate);
      }
    }
  ),

  onPlayerBitrateChanged: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setBitrate];
    },
    function handler(_, event, { getState }) {
      const state = getState();
      const bitrate = event.payload;

      if (!state.enabled || !state.videoAnalytics) return;

      if (bitrate) {
        state.videoAnalytics.reportPlaybackMetric(Conviva.Constants.Playback.BITRATE, bitrate);
      }
    }
  ),

  onError: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.error.setError];
    },
    function handler(actions, event, { getState, getStoreState }) {
      let state = getState();
      const storeState = getStoreState();

      if (!state.enabled) return;
      if (!state.videoAnalytics) actions.createSession();
      state = getState();

      state.videoAnalytics!.reportPlaybackFailed(`[Web Video Player] ${event.payload.message}`, {
        [Conviva.Constants.ASSET_NAME]: `[${storeState.config.videoGuid}] Error`,
      });
    }
  ),

  onPlayerDestroy: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.destroy.startType];
    },
    function handler(actions, _, { getState }) {
      const state = getState();

      if (state.enabled) {
        actions.reset();
        actions.setConvivaVideoAnalytics(null);
      }
    }
  ),

  /**
   * When the resume overlay is visible or not, report the corresponding playback event to optimize metrics before playing.
   */
  onResumeOverlayVisible: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.player.setResumeOverlayVisible];
    },
    function handler(_, { payload: isOverlayVisible }, { getState }) {
      const state = getState();
      if (state.videoAnalytics) {
        const playbackEvent = isOverlayVisible
          ? Conviva.Constants.Events.USER_WAIT_STARTED
          : Conviva.Constants.Events.USER_WAIT_ENDED;

        state.videoAnalytics.reportPlaybackEvent(playbackEvent);
      }
    }
  ),

  /**
   * Disable conviva when player starts casting
   */
  onChromecastCasting: thunkOn(
    function observer(_, storeActions) {
      return [storeActions.chromecast.setCastSession, storeActions.chromecast.setCasting];
    },
    function handler(actions, _, { getStoreState }) {
      const storeState = getStoreState();

      if (storeState.chromecast.castSession) {
        actions.destroySession();
        actions.reset();
        actions.setEnabled(false);
      }
    }
  ),
};
